import type { ModuleProgress } from '@/store/user';

/**
 * Scoring, ported verbatim from the 1.x app's
 * `src/app/utils/score-calculation.ts` so a learner's stars did not change
 * under them at the cutover. That app has since been removed; the behaviour it
 * defined is pinned by the tests beside this file.
 *
 * The argument is the number of *wrong* attempts (`nTried - nAnswered`), and
 * the first wrong attempt is forgiven — both quirks are deliberate.
 */
export function calculateStarRating(attempts: number): number {
  const excess = Math.max(0, attempts - 1);

  if (excess === 0) {
    return 5.0;
  }

  const penalty = 4.0 * Math.min(1.0, Math.log2(excess + 1) / 3);

  return Math.max(1.0, 5.0 - penalty);
}

/**
 * A module's star rating, or `undefined` while it is unfinished. Matches the
 * current `calculateTopicScore`: a score only exists once every counted slice
 * has been completed.
 */
export function calculateModuleScore(progress: ModuleProgress): number | undefined {
  if (progress.nCompleted !== progress.nSlices) {
    return undefined;
  }

  return calculateStarRating(progress.nTried - progress.nAnswered);
}

// --- Display ----------------------------------------------------------------
//
// A star rating is a float (1.6035…). These three are the only way one reaches
// a learner or GA4, so the completion card, the journey card, the screen-reader
// text and the analytics payload can never disagree about what it was.

/** One decimal place: the precision every score is shown and tracked at. */
export function roundScore(score: number): number {
  return Math.round(score * 10) / 10;
}

/** How many of the five stars a score fills: its rounded value, half up — 1.6 fills two. */
export function filledStars(score: number): number {
  return Math.min(5, Math.max(0, Math.round(roundScore(score))));
}

/**
 * A score as the current language writes it: `1.6`, `1,6` in German, and `5`
 * rather than `5.0`. Latin digits in every language, matching every other
 * number the app prints.
 */
export function formatScore(score: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 1, numberingSystem: 'latn' }).format(
    roundScore(score),
  );
}
